import React,{useEffect,useState}from"react";
import{Box,Button,Dialog,DialogActions,DialogContent,DialogTitle,Paper,Table,TableBody,TableCell,TableContainer,TableHead,TableRow,TextField,Typography}from"@mui/material";

const API_BASE="/api/purchases/nomina";

function formatMoney(value){
  return Number(value||0).toLocaleString("es-EC",{minimumFractionDigits:2,maximumFractionDigits:2});
}

function formatDate(value){
  if(!value)return"-";
  const parts=String(value).split("-");
  if(parts.length===3)return`${parts[2]}/${parts[1]}/${parts[0]}`;
  return value;
}

function employeeName(employee){
  if(!employee)return"";
  return employee.razon_social||employee.nombre_comercial||"";
}

export default function PayrollPayments({empresaId,payrollId,token,canCreate,canDelete,dashboardTheme,navigate,showError,showSuccess,showInfo,outlinedButtonSx,dangerButtonSx,parseError}){
  const[loading,setLoading]=useState(true);
  const[saving,setSaving]=useState(false);
  const[annulling,setAnnulling]=useState(false);
  const[nomina,setNomina]=useState(null);
  const[pagos,setPagos]=useState([]);
  const[paymentToAnnul,setPaymentToAnnul]=useState(null);
  const[motivoAnulacion,setMotivoAnulacion]=useState("");
  const[form,setForm]=useState({fecha_pago:new Date().toISOString().slice(0,10),efectivo:"",transferencia:"",codigo_transferencia:"",observacion:""});
  const efectivo=Number(form.efectivo||0);
  const transferencia=Number(form.transferencia||0);
  const totalPago=efectivo+transferencia;

  const loadPayments=async()=>{
    try{
      setLoading(true);
      const response=await fetch(`${API_BASE}/${payrollId}/pagos/?empresa_id=${empresaId}`,{headers:{Authorization:`Bearer ${token}`}});
      const data=await response.json();
      if(!response.ok){showError(parseError(data));return;}
      setNomina(data.nomina||null);
      setPagos(data.results||[]);
    }catch{
      showError("Error de conexión al cargar los pagos.");
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{loadPayments();},[empresaId,payrollId]);

  const savePayment=async()=>{
    if(totalPago<=0){showInfo("Debe ingresar un valor en efectivo, transferencia o ambos.");return;}
    if(transferencia>0&&!form.codigo_transferencia.trim()){showInfo("Debe ingresar el código de la transferencia.");return;}
    if(totalPago>Number(nomina?.saldo_pendiente||0)){showInfo("El pago no puede superar el saldo pendiente.");return;}
    try{
      setSaving(true);
      const response=await fetch(`${API_BASE}/${payrollId}/pagos/crear/`,{method:"POST",headers:{Authorization:`Bearer ${token}`,"Content-Type":"application/json"},body:JSON.stringify({empresa_id:empresaId,fecha_pago:form.fecha_pago,efectivo:form.efectivo||"0",transferencia:form.transferencia||"0",codigo_transferencia:form.codigo_transferencia,observacion:form.observacion})});
      const data=await response.json();
      if(!response.ok){showError(parseError(data));return;}
      showSuccess(data.mensaje||"Pago registrado correctamente.");
      setForm({fecha_pago:new Date().toISOString().slice(0,10),efectivo:"",transferencia:"",codigo_transferencia:"",observacion:""});
      loadPayments();
    }catch{
      showError("Error de conexión.");
    }finally{
      setSaving(false);
    }
  };

  const openAnnulDialog=pago=>{
    setPaymentToAnnul(pago);
    setMotivoAnulacion("");
  };

  const closeAnnulDialog=()=>{
    if(annulling)return;
    setPaymentToAnnul(null);
    setMotivoAnulacion("");
  };

  const annulPayment=async()=>{
    if(!paymentToAnnul)return;
    if(!motivoAnulacion.trim()){showInfo("Debe indicar el motivo de la anulación.");return;}
    try{
      setAnnulling(true);
      const response=await fetch(`${API_BASE}/pagos/${paymentToAnnul.id}/anular/`,{method:"POST",headers:{Authorization:`Bearer ${token}`,"Content-Type":"application/json"},body:JSON.stringify({empresa_id:empresaId,motivo:motivoAnulacion.trim()})});
      const data=await response.json();
      if(!response.ok){showError(parseError(data));return;}
      showSuccess(data.mensaje||"Pago anulado correctamente.");
      setPaymentToAnnul(null);
      setMotivoAnulacion("");
      loadPayments();
    }catch{
      showError("Error de conexión.");
    }finally{
      setAnnulling(false);
    }
  };

  if(loading)return<Typography sx={{py:4,textAlign:"center",color:dashboardTheme.muted_text_color}}>Cargando pagos...</Typography>;
  if(!nomina)return null;

  return(
    <>
      <Box sx={{display:"flex",alignItems:{xs:"stretch",sm:"center"},justifyContent:"space-between",flexDirection:{xs:"column",sm:"row"},gap:1.5,mb:2}}>
        <Box>
          <Typography sx={{fontSize:{xs:24,sm:26,md:30},fontWeight:600,color:dashboardTheme.primary_color,lineHeight:1.2}}>Pagos de nómina</Typography>
          <Typography sx={{fontSize:13,fontWeight:500,color:dashboardTheme.muted_text_color,mt:.4}}>{employeeName(nomina.empleado)}</Typography>
        </Box>
        <Button variant="outlined" onClick={()=>navigate(`/admin-panel/nomina/${payrollId}`)} sx={outlinedButtonSx}>Regresar</Button>
      </Box>

      <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"repeat(3,minmax(0,1fr))"},gap:1.2,mb:2}}>
        <Paper elevation={0} sx={{p:1.5,border:`1px solid ${dashboardTheme.border_color}`,borderRadius:"12px",bgcolor:dashboardTheme.card_background}}><Typography sx={{fontSize:12.5,color:dashboardTheme.muted_text_color}}>Total nómina</Typography><Typography sx={{fontSize:21,fontWeight:700,color:dashboardTheme.text_color}}>${formatMoney(nomina.total)}</Typography></Paper>
        <Paper elevation={0} sx={{p:1.5,border:`1px solid ${dashboardTheme.border_color}`,borderRadius:"12px",bgcolor:dashboardTheme.card_background}}><Typography sx={{fontSize:12.5,color:dashboardTheme.muted_text_color}}>Pagado</Typography><Typography sx={{fontSize:21,fontWeight:700,color:dashboardTheme.text_color}}>${formatMoney(nomina.total_pagado)}</Typography></Paper>
        <Paper elevation={0} sx={{p:1.5,border:`1px solid ${dashboardTheme.border_color}`,borderRadius:"12px",bgcolor:dashboardTheme.card_background}}><Typography sx={{fontSize:12.5,color:dashboardTheme.muted_text_color}}>Saldo pendiente</Typography><Typography sx={{fontSize:21,fontWeight:700,color:Number(nomina.saldo_pendiente)>0?"#D97706":dashboardTheme.text_color}}>${formatMoney(nomina.saldo_pendiente)}</Typography></Paper>
      </Box>

      {canCreate&&nomina.estado==="REGISTRADO"&&Number(nomina.saldo_pendiente)>0&&<Paper elevation={0} sx={{...dashboardTheme.tableStyles.wrapper,p:{xs:1.5,md:2},mb:2}}>
        <Typography sx={{fontSize:18,fontWeight:700,color:dashboardTheme.text_color,mb:1.5}}>Registrar pago</Typography>
        <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",md:"repeat(2,minmax(0,1fr))"},gap:1.5}}>
          <TextField fullWidth size="small" type="date" label="Fecha de pago" value={form.fecha_pago} onChange={e=>setForm(prev=>({...prev,fecha_pago:e.target.value}))} InputLabelProps={{shrink:true}} sx={dashboardTheme.inputStyles}/>
          <TextField fullWidth size="small" type="number" label="Efectivo" value={form.efectivo} inputProps={{min:"0",step:"0.01"}} onChange={e=>setForm(prev=>({...prev,efectivo:e.target.value}))} sx={dashboardTheme.inputStyles}/>
          <TextField fullWidth size="small" type="number" label="Transferencia" value={form.transferencia} inputProps={{min:"0",step:"0.01"}} onChange={e=>setForm(prev=>({...prev,transferencia:e.target.value}))} sx={dashboardTheme.inputStyles}/>
          <TextField fullWidth size="small" label="Código de transferencia" value={form.codigo_transferencia} disabled={transferencia<=0} onChange={e=>setForm(prev=>({...prev,codigo_transferencia:e.target.value}))} sx={dashboardTheme.inputStyles}/>
        </Box>
        <TextField fullWidth size="small" label="Observación" multiline minRows={2} value={form.observacion} onChange={e=>setForm(prev=>({...prev,observacion:e.target.value}))} sx={{mt:1.5,...dashboardTheme.inputStyles}}/>
        <Box sx={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:2,flexWrap:"wrap",mt:2}}>
          <Box><Typography sx={{fontSize:12.5,color:dashboardTheme.muted_text_color}}>Total de este pago</Typography><Typography sx={{fontSize:21,fontWeight:700,color:dashboardTheme.primary_color}}>${formatMoney(totalPago)}</Typography></Box>
          <Button variant="contained" onClick={savePayment} disabled={saving} sx={dashboardTheme.buttonStyles.contained}>{saving?"Guardando...":"Registrar pago"}</Button>
        </Box>
      </Paper>}

      <Paper elevation={0} sx={dashboardTheme.tableStyles.wrapper}>
        <Box sx={dashboardTheme.tableStyles.tableArea}>
          <TableContainer sx={dashboardTheme.tableStyles.container}>
            <Table size="small" sx={dashboardTheme.tableStyles.table}>
              <TableHead><TableRow sx={dashboardTheme.tableStyles.headRow}><TableCell sx={dashboardTheme.tableStyles.headCell}>Fecha</TableCell><TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Efectivo</TableCell><TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Transferencia</TableCell><TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:170}}>Código</TableCell><TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Total</TableCell><TableCell sx={dashboardTheme.tableStyles.headCell}>Forma</TableCell><TableCell sx={dashboardTheme.tableStyles.headCell}>Estado</TableCell>{canDelete&&<TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Acciones</TableCell>}</TableRow></TableHead>
              <TableBody>
                {pagos.map(pago=><TableRow key={pago.id} sx={dashboardTheme.tableStyles.bodyRow}>
                  <TableCell sx={{whiteSpace:"nowrap"}}>{formatDate(pago.fecha_pago)}</TableCell>
                  <TableCell align="right">${formatMoney(pago.efectivo)}</TableCell>
                  <TableCell align="right">${formatMoney(pago.transferencia)}</TableCell>
                  <TableCell>{pago.codigo_transferencia||"-"}</TableCell>
                  <TableCell align="right"><Typography sx={{fontSize:13.5,fontWeight:700,color:dashboardTheme.text_color}}>${formatMoney(pago.total)}</Typography></TableCell>
                  <TableCell>{pago.forma_pago==="MIXTO"?"Mixto":pago.forma_pago==="TRANSFERENCIA"?"Transferencia":"Efectivo"}</TableCell>
                  <TableCell><Typography sx={{fontSize:13.5,fontWeight:600,color:pago.estado==="ANULADO"?"#D32F2F":dashboardTheme.text_color}}>{pago.estado==="ANULADO"?"Anulado":"Activo"}</Typography></TableCell>
                  {canDelete&&<TableCell align="right">{pago.estado==="ACTIVO"&&<Button variant="outlined" size="small" onClick={()=>openAnnulDialog(pago)} sx={dangerButtonSx}>Anular</Button>}</TableCell>}
                </TableRow>)}
                {pagos.length===0&&<TableRow><TableCell colSpan={canDelete?8:7} align="center" sx={dashboardTheme.tableStyles.emptyCell}>No hay pagos registrados</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      <Dialog open={Boolean(paymentToAnnul)} onClose={closeAnnulDialog} fullWidth maxWidth="sm" PaperProps={{elevation:0,sx:{bgcolor:dashboardTheme.card_background,color:dashboardTheme.text_color,border:`1px solid ${dashboardTheme.border_color}`,borderRadius:"14px",boxShadow:"none"}}}>
        <DialogTitle sx={{fontWeight:700,color:dashboardTheme.text_color}}>Anular pago</DialogTitle>
        <DialogContent>
          <Typography sx={{fontSize:13.5,color:dashboardTheme.muted_text_color,mb:1.5}}>El pago quedará anulado y dejará de contar dentro del total pagado.</Typography>
          <TextField autoFocus fullWidth size="small" label="Motivo de anulación" multiline minRows={3} value={motivoAnulacion} onChange={e=>setMotivoAnulacion(e.target.value)} sx={dashboardTheme.inputStyles}/>
        </DialogContent>
        <DialogActions sx={{p:2,pt:1}}>
          <Button variant="outlined" onClick={closeAnnulDialog} disabled={annulling} sx={outlinedButtonSx}>Cancelar</Button>
          <Button variant="contained" onClick={annulPayment} disabled={annulling} sx={{minHeight:40,px:2.4,borderRadius:"12px",textTransform:"none",fontWeight:700,bgcolor:"#D32F2F",boxShadow:"none","&:hover":{bgcolor:"#B71C1C",boxShadow:"none"}}}>{annulling?"Anulando...":"Anular pago"}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}